"use client";

import React, { useMemo, useState } from "react";
import { ResponsiveModal, ModalContainer, MODAL_IDS, Calendar } from "@wtc/ui";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useUpdateDeal } from "@/features/deals/hooks/use-deals-hooks";
import {
  setScheduledFollowupWeek,
  clearScheduledFollowup,
  getScheduledFollowupWeek,
} from "@/lib/utils/task/scheduled-followup-tagging";
import { getISOWeekString, isoWeekToMondayUtc } from "@/lib/utils/task/iso-week";
import type { ScheduleDealFollowUpModalData } from "@/lib/modals/data/modal-data-types";
import { useTodayUtcMidnight } from "@/lib/hooks/use-today-utc-midnight";

// Global-mount modal (metadata-driven registry). Self-contained: receives dealId
// + current tags via the modal-stack payload and writes the merged tag array via
// the codegen-typed useUpdateDeal hook — no DetailActionContext.
// See .claude/rules/GLOBAL_MODAL_NO_ACTION_CONTEXT.md.

export function SetFollowUpDateModal() {
  return (
    <ModalContainer<ScheduleDealFollowUpModalData> modalId={MODAL_IDS.deals.scheduleFollowUp}>
      {({ isOpen, data, onClose }) => (
        <SetFollowUpDateModalContent isOpen={isOpen} data={data} onClose={onClose} />
      )}
    </ModalContainer>
  );
}

interface SetFollowUpDateModalContentProps {
  isOpen: boolean;
  data?: ScheduleDealFollowUpModalData;
  onClose: () => void;
}

/** "2026-W30" → "Week 30, 2026 (Jul 20)". */
function formatWeek(week: string): string {
  const match = /^(\d{4})-W(\d{2})$/.exec(week);
  if (!match) return week;
  const monday = isoWeekToMondayUtc(week);
  const dateLabel = monday
    ? monday.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })
    : "";
  return `Week ${Number(match[2])}, ${match[1]}${dateLabel ? ` (${dateLabel})` : ""}`;
}

function SetFollowUpDateModalContent({ isOpen, data, onClose }: SetFollowUpDateModalContentProps) {
  const updateDeal = useUpdateDeal();
  const [selected, setSelected] = useState<Date | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);

  const currentTags = data?.tags ?? [];
  const dealId = data?.dealId;

  const existingWeek = useMemo(() => getScheduledFollowupWeek(currentTags), [currentTags]);

  const previewWeek = useMemo(
    () => (selected ? getISOWeekString(selected) : null),
    [selected]
  );

  const todayUtcMidnight = useTodayUtcMidnight();

  if (!dealId) return null;

  const handleSet = async () => {
    if (!selected) {
      toast.error("Pick a date to follow up on");
      return;
    }
    setIsSaving(true);
    try {
      const updatedTags = setScheduledFollowupWeek(currentTags, selected);
      await updateDeal.mutateAsync({ id: dealId, tags: updatedTags });
      toast.success(`Follow-up scheduled for ${previewWeek ? formatWeek(previewWeek) : "the selected week"}`);
      onClose();
    } catch (error) {
      console.error("Error scheduling follow-up:", error);
      toast.error("Failed to schedule follow-up");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClear = async () => {
    setIsSaving(true);
    try {
      const updatedTags = clearScheduledFollowup(currentTags);
      await updateDeal.mutateAsync({ id: dealId, tags: updatedTags });
      toast.success("Scheduled follow-up cleared");
      onClose();
    } catch (error) {
      console.error("Error clearing scheduled follow-up:", error);
      toast.error("Failed to clear scheduled follow-up");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ResponsiveModal
      globalModalId={MODAL_IDS.deals.scheduleFollowUp}
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Set follow-up date"
      description={
        data?.dealTitle
          ? `Schedule when to follow up on "${data.dealTitle}". Visible to the team; reminders pause until that week.`
          : "Schedule a week to follow up on this deal. Visible to the team; reminders pause until that week."
      }
      maxWidth="sm"
    >
      <div className="p-4 space-y-4">
        {existingWeek && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 p-3">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Currently scheduled: {formatWeek(existingWeek)}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClear}
              disabled={isSaving}
            >
              Clear
            </Button>
          </div>
        )}

        <div className="flex justify-center">
          {/* `todayUtcMidnight` resolves on the client (see useTodayUtcMidnight).
              Rendering the picker before then would anchor it to a placeholder. */}
          {todayUtcMidnight && (
          <Calendar
            mode="single"
            selected={selected}
            onSelect={setSelected}
            disabled={{ before: todayUtcMidnight }}
            defaultMonth={todayUtcMidnight}
          />
          )}
        </div>

        <div className="rounded-lg border bg-card-50 p-3 min-h-[3rem]">
          {previewWeek ? (
            <p className="text-sm text-card-foreground">
              Follow up during <span className="font-medium">{formatWeek(previewWeek)}</span>. This deal
              stays out of “Not Followed Up This Week” until then.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">Pick a date to schedule the follow-up week.</p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSet} disabled={isSaving || !selected}>
            {isSaving ? "Saving…" : "Set follow-up"}
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
}
